import { useState } from 'react';
import useAssignVehicleToDriver from '../../../../API/hooks/vehicles/useAssignVehicleToDriver';
import useGetVehicles from '../../../../API/hooks/vehicles/useGetVehicles';
import { Vehicle as VehicleType } from '../../../../API/types/Vehicles';
import DropdownFilter from '../../../../components/common/DropdownFilter/DropdownFilter';
import { FilterConfig } from '../../../../components/common/DropdownFilter/types';
import Icon from '../../../../components/common/Icon/Icon';
import { IconKeys } from '../../../../components/common/Icon/IconKeys';
import Tags from '../../../../components/common/Tags';
import { Button } from '../../../../components/ui/Button/Button';
import { queryFilters } from '../../../../utils/queryFilters';
import S from './AssignVehicleToDriverStyles';
import { generateUnassignedVehiclesTitle } from '../helpers';
import Vehicle from './Vehicle';
import useVehicleThumbnail from '../../../../theme/useVehicleThumbnail';

type Props = {
  onCancel: () => void;
  driverId: string;
  name: string;
  licenseName: string;
  avatar: string;
  fleetId: string;
};

const SEARCH_CONFIG = [
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      // format of key should be [param]_[order<ASC|DESC>]
      { score_DESC: 'Score: most' },
      { score_ASC: 'Score: least' },
    ],
  },
] as FilterConfig[];

function AssignVehicleToDriver({
  onCancel,
  driverId,
  licenseName,
  avatar,
  fleetId,
  name,
}: Props): JSX.Element {
  const vehicleThumbnail = useVehicleThumbnail();
  const [filters, setFilters] = useState<Record<string, string[]>>({ driverId: ['unassigned'] });

  const { data, isLoading, isFetching } = useGetVehicles(fleetId, queryFilters(filters));
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleType | null>(null);
  const [assignError, setAssignError] = useState('');

  const onSubmitSuccess = (): void => {
    setSelectedVehicle(null);
    setAssignError('');
    onCancel();
  };
  const onSubmitFail = (error: string): void => {
    setAssignError(error);
  };

  const { mutate } = useAssignVehicleToDriver(onSubmitSuccess, onSubmitFail);

  const handleVehicleAssignment = (): void => mutate({ driverId, fleetId, vehicleId: selectedVehicle!._id });

  const handleVehicleSelection = (vehicle: VehicleType): void => {
    setSelectedVehicle(() => vehicle);
  };

  return (
    <S.Wrapper>
      <div className="titles">
        <div className="action-bar">
          <div className="bar">
            <div className="assignment-container">
              <div className="driver-overview">
                <Tags.ItemOverview
                  avatar={avatar}
                  avatarAlt="driver"
                  title={name}
                  subtitle={licenseName}
                />
              </div>
              <Icon iconKey={IconKeys.ARROW_RIGHT} />
              <div className="vehicle-overview">
                {selectedVehicle
                  ? (
                    <Tags.Vehicle
                      imageSrc={vehicleThumbnail}
                      licensePlateNumber={selectedVehicle.licensePlateNumber}
                      model={`${selectedVehicle.manufacturer ? (`${selectedVehicle.manufacturer} `) : ''}${selectedVehicle.model ? (`${selectedVehicle.model} `) : ''}`}
                    />
                  )
                  : <div className="vehicle-placeholder"> Choose vehicle from below </div>}
              </div>
            </div>
            <div className="buttons-container">
              <Button
                className="cancel-btn"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                className="confirm-btn"
                disabled={!selectedVehicle}
                onClick={handleVehicleAssignment}
              >
                Assign vehicle
              </Button>
            </div>
          </div>
          {!!assignError && <div className="error"> {assignError} </div>}
        </div>
        <div className="vehicles-toolbar">
          <h3>{generateUnassignedVehiclesTitle(data?.results)}</h3>
          {
            SEARCH_CONFIG.map((filterConfig: FilterConfig) => (
              <DropdownFilter
                key={filterConfig.type}
                config={filterConfig}
                filters={filters}
                setFilters={setFilters}
              />
            ))
          }
        </div>
      </div>
      <div className="vehicles-list">
        {isLoading && <div> Loading... </div>}
        {!isLoading && data?.results && (
          <ul className="list-container">
              {!!data.results.length
                && data.results.map((item: VehicleType) => (
                  <Vehicle
                    vehicle={item}
                    isChecked={selectedVehicle?._id === item._id}
                    selectVehicle={handleVehicleSelection}
                  />
                ))}
              {!isFetching && !data.results.length && <div className="no-data">No vehicles found</div>}
          </ul>
        )}
      </div>
    </S.Wrapper>
  );
}

export default AssignVehicleToDriver;
